import { salesTileKeyType } from 'components/pages/GrowthAnalysis';
import { colorConstants } from 'config/colorConstants';
import _ from 'lodash';
import { fetchCurrency, getAbsoluteChange, getPercentageChangeV2 } from 'utils/helperMethods';
import { attributeSalesKeyMapping } from './attributeSalesDataParser';

const tabMapping = {
    incremental: {
        tabs: ['Base', 'Incremental'],
        data: ['base', 'incremental'],
    },
    promo: {
        tabs: ['Promo', 'Non-promo'],
        data: ['promo', 'nonPromo'],
    },
};

export const totalString = 'TOTAL ';
//only show dollar symbol for value and profit

function isDollar(key: any, config: any) {
    return ['val', 'profit'].includes(key) ? fetchCurrency(config) : '';
}

export function attributeSalesTableParser(
    data: any,
    from: salesTileKeyType = 'sales',
    volTile: any,
    config: any,
    graphDataKey: 'graphData' | 'graph_data' = 'graphData',
    salesData: 'salesData' | 'sales_data' = 'salesData',
    attributeTypeKey: 'attributeType' | 'attribute_type' = 'attributeType',
) {
    const { growthAnalysis } = config;
    const showTotal = growthAnalysis?.showTotal ?? true;
    const tableLabelKeys = growthAnalysis?.tableLabelKeys ?? {};
    const currentYearLabel = growthAnalysis?.currentYearLabel ?? '';
    const yearAgoLabel = growthAnalysis?.yearAgoLabel ?? '';

    const tableLabelMapping: any = {
        sales_vol: tableLabelKeys?.sales_vol ?? 'Volume Sales ',
        sales_val: tableLabelKeys?.sales_val ?? 'Value Sales ',
        sales_units: tableLabelKeys?.sales_units ?? 'Unit Sales ',
        sales_profit: tableLabelKeys?.sales_profit ?? 'Gross Profit ',
        sales_vol_change: tableLabelKeys?.sales_vol_change ?? '',
        sales_val_change: tableLabelKeys?.sales_val_change ?? '',
        sales_units_change: tableLabelKeys?.sales_units_change ?? 'Unit Sales ',
        sales_profit_change: tableLabelKeys?.sales_profit_change ?? 'Gross Profit ',
        incremental_vol: tableLabelKeys?.incremental_vol ?? 'Volume Sales ',
        incremental_val: tableLabelKeys?.incremental_val ?? 'Value Sales ',
        incremental_profit: tableLabelKeys?.incremental_profit ?? 'Profit ',
        incremental_units: tableLabelKeys?.incremental_units ?? 'Unit Sales ',
        incremental_vol_change: tableLabelKeys?.incremental_vol_change ?? 'Sales ',
        incremental_val_change: tableLabelKeys?.incremental_val_change ?? 'Sales ',
        incremental_profit_change: tableLabelKeys?.incremental_profit_change ?? 'Profit ',
        incremental_units_change: tableLabelKeys?.incremental_units_change ?? 'Unit Sales ',
        promo_vol: tableLabelKeys?.promo_vol ?? 'Volume Sales ',
        promo_val: tableLabelKeys?.promo_val ?? 'Value Sales ',
        promo_profit: tableLabelKeys?.promo_profit ?? 'Profit ',
        promo_units: tableLabelKeys?.promo_units ?? 'Unit Sales ',
        promo_vol_change: tableLabelKeys?.promo_vol_change ?? 'Sales ',
        promo_val_change: tableLabelKeys?.promo_val_change ?? 'Sales ',
        promo_profit_change: tableLabelKeys?.promo_profit_change ?? 'Profit ',
        promo_units_change: tableLabelKeys?.promo_units_change ?? 'Unit Sales ',
        depletion_frontline: tableLabelKeys?.depletion_frontline ?? 'Frontline Sales ',
        depletion_frontline_change: tableLabelKeys?.depletion_frontline_change ?? '',
        depletion_net_change: tableLabelKeys?.depletion_net_change ?? '',
        depletion_net: tableLabelKeys?.depletion_net ?? 'Net Sales ',
    };

    let result: any[] = [];
    //only show dollar symbol for value and profit
    const showDollar = isDollar(volTile.key, config);
    try {
        //for sales tab
        if (from === 'sales' || from === 'depletion') {
            const typeValue = tableLabelMapping[`${from}_${volTile.key}`] || '';
            let salesCpData: any = { type: typeValue + currentYearLabel, [totalString]: null },
                salesYaData: any = { type: typeValue + yearAgoLabel, [totalString]: null },
                salesAbsData: any = {
                    type: typeValue + 'Abs Change',
                    [totalString]: null,
                },
                salesPercentageData: any = {
                    type: (tableLabelMapping[`${from}_${volTile.key}_change`] || '') + '% Change',
                    [totalString]: null,
                };
            let additionalDataTemp: any = {};

            data.forEach((item: any) => {
                //SALES
                salesCpData[item[attributeTypeKey] + ' '] = [
                    { data: item[graphDataKey][0][attributeSalesKeyMapping[salesData]], unit: showDollar },
                ];
                salesYaData[item[attributeTypeKey] + ' '] = [
                    { data: item[graphDataKey][1][attributeSalesKeyMapping[salesData]], unit: showDollar },
                ];
                salesAbsData[item[attributeTypeKey] + ' '] = [
                    {
                        data: getAbsoluteChange(
                            item[graphDataKey][1][attributeSalesKeyMapping[salesData]],
                            item[graphDataKey][0][attributeSalesKeyMapping[salesData]],
                        ),
                        unit: showDollar,
                    },
                ];
                salesPercentageData[item[attributeTypeKey] + ' '] = [
                    {
                        data: getPercentageChangeV2(
                            item[graphDataKey][1][attributeSalesKeyMapping[salesData]],
                            item[graphDataKey][0][attributeSalesKeyMapping[salesData]],
                        ),
                        unit: '%',
                    },
                ];
                if (item?.additionalData) {
                    //additional data
                    generateAdditionalData(item, additionalDataTemp, from, volTile, config);
                }
            });
            //converting object to array
            const additionalData = Object.keys(additionalDataTemp).map((item: any) => {
                return {
                    type: item,
                    ...additionalDataTemp[item],
                };
            });
            result.push(
                salesCpData,
                salesYaData,
                salesAbsData,
                salesPercentageData,
                ...additionalData, //vol
            );
            //for incremental and promo
        } else {
            const vol: any[] = []; //vol

            const mapping = tabMapping[from];
            //looping 2 times to show grouped charts
            mapping.tabs.map((item: string) => {
                const data = generateDefaultData(
                    item,
                    volTile,
                    from,
                    tableLabelMapping,
                    currentYearLabel,
                    yearAgoLabel,
                );
                vol.push(...data);
            });
            //only show for incremental + vol+val

            if (from === 'incremental' && (volTile.key === 'vol' || volTile.key === 'val')) {
                const incrementalRemainingData = [
                    {
                        type: `% ${volTile.label} Incremental ${currentYearLabel}`,
                        [totalString]: null,
                    },
                    {
                        type: `% ${volTile.label} Incremental ${yearAgoLabel}`,
                        [totalString]: null,
                    },
                ];
                vol.push(...incrementalRemainingData);
            }
            //only show for promo + vol+val

            if (from === 'promo' && (volTile.key === 'vol' || volTile.key === 'val')) {
                const promoRemainingData = [
                    {
                        type: `% ${volTile.label} Promo ${currentYearLabel}`,
                        [totalString]: null,
                    },
                    {
                        type: `% ${volTile.label} Promo ${yearAgoLabel}`,
                        [totalString]: null,
                    },
                ];
                vol.push(...promoRemainingData);
            }
            let additionalDataTemp: any = {};
            let dataKey = tabMapping[from]['data'][0] + 'Data';
            data.forEach((item: any) => {
                dataKey = tabMapping[from]['data'][0] + 'Data';
                // 1st HALF
                //VOL
                vol[0][item.attributeType + ' '] = [
                    { data: item[graphDataKey][0][attributeSalesKeyMapping[dataKey]], unit: showDollar },
                ];
                vol[1][item.attributeType + ' '] = [
                    { data: item[graphDataKey][1][attributeSalesKeyMapping[dataKey]], unit: showDollar },
                ];
                vol[2][item.attributeType + ' '] = [
                    {
                        data: getAbsoluteChange(
                            item[graphDataKey][1][attributeSalesKeyMapping[dataKey]],
                            item[graphDataKey][0][attributeSalesKeyMapping[dataKey]],
                        ),
                        unit: showDollar,
                    },
                ];
                vol[3][item.attributeType + ' '] = [
                    {
                        data: getPercentageChangeV2(
                            item[graphDataKey][1][attributeSalesKeyMapping[dataKey]],
                            item[graphDataKey][0][attributeSalesKeyMapping[dataKey]],
                        ),
                        unit: '%',
                    },
                ];

                // 2ND  HALF
                dataKey = tabMapping[from]['data'][1] + 'Data';
                //VOL
                vol[4][item.attributeType + ' '] = [
                    { data: item[graphDataKey][0][attributeSalesKeyMapping[dataKey]], unit: showDollar },
                ];
                vol[5][item.attributeType + ' '] = [
                    { data: item[graphDataKey][1][attributeSalesKeyMapping[dataKey]], unit: showDollar },
                ];
                vol[6][item.attributeType + ' '] = [
                    {
                        data: getAbsoluteChange(
                            item[graphDataKey][1][attributeSalesKeyMapping[dataKey]],
                            item[graphDataKey][0][attributeSalesKeyMapping[dataKey]],
                        ),
                        unit: showDollar,
                    },
                ];
                vol[7][item.attributeType + ' '] = [
                    {
                        data: getPercentageChangeV2(
                            item[graphDataKey][1][attributeSalesKeyMapping[dataKey]],
                            item[graphDataKey][0][attributeSalesKeyMapping[dataKey]],
                        ),
                        unit: '%',
                    },
                ];

                //only show for incremental + vol+val
                if (from === 'incremental' && (volTile.key === 'vol' || volTile.key === 'val')) {
                    const secondKey = tabMapping[from]['data'][1];
                    const firstKey = tabMapping[from]['data'][0];
                    const secondDataKey = `${secondKey}Data`;
                    const firstDataKey = `${firstKey}Data`;
                    //VOL
                    vol[8] = {
                        ...vol[8],
                        [item.attributeType + ' ']: [
                            {
                                data:
                                    (item[graphDataKey][0][attributeSalesKeyMapping[secondDataKey]] /
                                        (item[graphDataKey][0][attributeSalesKeyMapping[firstDataKey]] +
                                            item[graphDataKey][0][attributeSalesKeyMapping[secondDataKey]])) *
                                    100,
                                unit: '%',
                            },
                        ],
                    };
                    vol[9] = {
                        ...vol[9],
                        [item.attributeType + ' ']: [
                            {
                                data:
                                    (item[graphDataKey][1][attributeSalesKeyMapping[secondDataKey]] /
                                        (item[graphDataKey][1][attributeSalesKeyMapping[firstDataKey]] +
                                            item[graphDataKey][1][attributeSalesKeyMapping[secondDataKey]])) *
                                    100,
                                unit: '%',
                            },
                        ],
                    };
                }
                //only show for promo + vol+val
                if (from === 'promo' && (volTile.key === 'vol' || volTile.key === 'val')) {
                    const secondKey = tabMapping[from]['data'][1];
                    const firstKey = tabMapping[from]['data'][0];
                    const secondDataKey = `${secondKey}Data`;
                    const firstDataKey = `${firstKey}Data`;
                    //VOL
                    vol[8] = {
                        ...vol[8],
                        [item.attributeType + ' ']: [
                            {
                                data:
                                    (item[graphDataKey][0][attributeSalesKeyMapping[firstDataKey]] /
                                        (item[graphDataKey][0][attributeSalesKeyMapping[firstDataKey]] +
                                            item[graphDataKey][0][attributeSalesKeyMapping[secondDataKey]])) *
                                    100,
                                unit: '%',
                            },
                        ],
                    };
                    vol[9] = {
                        ...vol[9],
                        [item.attributeType + ' ']: [
                            {
                                data:
                                    (item[graphDataKey][1][attributeSalesKeyMapping[firstDataKey]] /
                                        (item[graphDataKey][1][attributeSalesKeyMapping[firstDataKey]] +
                                            item[graphDataKey][1][attributeSalesKeyMapping[secondDataKey]])) *
                                    100,
                                unit: '%',
                            },
                        ],
                    };
                }
                //additional data
                generateAdditionalData(item, additionalDataTemp, from, volTile, config);
            });
            //converting object to array
            const additionalData = Object.keys(additionalDataTemp).map((item: any) => {
                return {
                    type: item,
                    ...additionalDataTemp[item],
                };
            });
            result = [...vol, ...additionalData];
        }
        //if no show total, then remove total key
        if (!showTotal) {
            result?.forEach((item) => {
                delete item?.[totalString];
            });
        }
        return result;
    } catch (error) {
        console.log(error, 'logging e');
        return result;
    }
}

const generateAdditionalData = (item: any, additionalDataTemp: any, from: string, volTile: any, config: any) => {
    //only show dollar symbol for value and profit
    const showDollar = isDollar(volTile.key, config);
    const tableKeyUnitMapping = config?.growthAnalysis?.tableKeyUnitMapping;
    item?.additionalData[from]?.forEach((kpi: any, index: number) => {
        const unit =
            tableKeyUnitMapping && kpi?.labelName in tableKeyUnitMapping
                ? tableKeyUnitMapping?.[kpi?.labelName]?.unit
                : showDollar;
        const showPercentageChange =
            tableKeyUnitMapping && kpi?.labelName in tableKeyUnitMapping
                ? tableKeyUnitMapping?.[kpi?.labelName]?.includePercentageChange
                : true;
        const label =
            tableKeyUnitMapping &&
            kpi?.labelName in tableKeyUnitMapping &&
            'label' in tableKeyUnitMapping[kpi?.labelName]
                ? tableKeyUnitMapping[kpi?.labelName]['label']
                : kpi.labelName;
        //for odd indices, calculate abs change and percentage change
        //add to the additionaldata object
        if (index % 2 !== 0) {
            additionalDataTemp[label] =
                label in additionalDataTemp
                    ? {
                          ...additionalDataTemp[label],
                          [item.attributeType + ' ']: formatAdditionalData(kpi.value, unit),
                      }
                    : { [item.attributeType + ' ']: formatAdditionalData(kpi.value, unit) };
            const trimmedString = String(label).substring(0, label.lastIndexOf(' ')); //removes the last word
            //abs change
            const absValue = getAbsoluteChange(kpi.value, item?.additionalData[from][index - 1].value);
            //percentage change
            const percentageChangeValue = getPercentageChangeV2(kpi.value, item?.additionalData[from][index - 1].value);
            //abs change and % change string
            const absChangeString = `${trimmedString} Abs Change`;
            const percentageChangeString = `${trimmedString} % Change`;
            if (showPercentageChange) {
                additionalDataTemp[percentageChangeString] =
                    percentageChangeString in additionalDataTemp
                        ? {
                              ...additionalDataTemp[percentageChangeString],
                              [item.attributeType + ' ']: formatAdditionalData(percentageChangeValue, '%'),
                          }
                        : { [item.attributeType + ' ']: formatAdditionalData(percentageChangeValue, '%') };
            }
            additionalDataTemp[absChangeString] =
                absChangeString in additionalDataTemp
                    ? {
                          ...additionalDataTemp[absChangeString],
                          [item.attributeType + ' ']: formatAdditionalData(absValue, unit === '%' ? '' : unit),
                      }
                    : { [item.attributeType + ' ']: formatAdditionalData(absValue, unit === '%' ? '' : unit) };
        } else {
            //add to the additionaldata object
            additionalDataTemp[label] =
                label in additionalDataTemp
                    ? {
                          ...additionalDataTemp[label],
                          [item.attributeType + ' ']: formatAdditionalData(kpi.value, unit),
                      }
                    : { [item.attributeType + ' ']: formatAdditionalData(kpi.value, unit) };
        }
    });
};

const generateDefaultData = (
    item: string,
    volTile: any,
    from: salesTileKeyType = 'sales',
    tableLabelMapping: any,
    currentYearLabel: string,
    yearAgoLabel: string,
) => {
    const cpData: any = {
            type: item + ' ' + tableLabelMapping[`${from}_${volTile.key}`] + currentYearLabel,
            [totalString]: null,
        },
        yaData: any = {
            type: item + ' ' + tableLabelMapping[`${from}_${volTile.key}`] + yearAgoLabel,
            [totalString]: null,
        },
        absData: any = {
            type: item + ' ' + tableLabelMapping[`${from}_${volTile.key}`] + 'Abs Change',
            [totalString]: null,
        },
        percentageData: any = {
            type: item + ' ' + tableLabelMapping[`${from}_${volTile.key}_change`] + `% Change`,
            [totalString]: null,
        };
    return [cpData, yaData, absData, percentageData];
};

const formatAdditionalData = (val: number, unit: string = '') => {
    return [{ data: val, unit: unit }];
};
